import { Component, OnDestroy, ViewChild, Input, Output, EventEmitter } from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { Subscription, Observable } from 'rxjs'
import { Table } from 'primeng/table'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from '../../services/account.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { GroupManagementItem } from '../group-management/group-management.component'
import { ApiService } from 'src/app/services/api.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { RoleLoadingService } from 'src/app/services/role-loading.service'
import { FormValidationService } from 'src/app/services/form-validation.service'
import { BaseGroupModal, RoleItem, UserItem } from '../shared/base-group-modal'
import { ModalService, ModalController } from '../../services/modal.service'
import { handleArpioError } from 'src/utils/handle-error'

@Component({
  selector: 'app-edit-group-modal',
  templateUrl: './edit-group-modal.component.html',
  standalone: false,
})
export class EditGroupModalComponent extends BaseGroupModal implements OnDestroy, ModalController {
  @ViewChild('editGroupModal') modal: ModalDialogComponent
  @ViewChild('deleteGroupDialog', { static: true }) deleteGroupDialog: ModalDialogComponent
  @ViewChild('rolesTable') rolesTable: Table
  @ViewChild('usersTable') usersTable: Table
  @Input() group: GroupManagementItem | null = null
  @Output() groupUpdated = new EventEmitter<GroupManagementItem>()

  // Delete group dialog
  groupToDelete: GroupManagementItem | null = null

  constructor(
    protected formBuilder: FormBuilder,
    protected accountService: AccountService,
    protected uiService: UiService,
    protected apiService: ApiService,
    protected accessLevelService: AccessLevelService,
    protected roleLoadingService: RoleLoadingService,
    protected formValidationService: FormValidationService,
    private modalService: ModalService
  ) {
    super(
      formBuilder,
      accountService,
      apiService,
      uiService,
      accessLevelService,
      roleLoadingService,
      formValidationService
    )
    // Register with modal service
    this.modalService.register(this, this)
  }

  ngOnDestroy() {
    this.cleanup()
    // Unregister from modal service
    this.modalService.unregister(this)
  }

  open(group: GroupManagementItem) {
    this.group = group
    this.populateForm()
    this.loadRolesAndUsers().then(() => {
      this.loadGroupRolesAndUsers()
      this.modal.open()
    })
  }

  close() {
    this.modal.close()
    this.resetForm()
  }

  isModalOpen(): boolean {
    return this.modal && this.modal.isOpen
  }

  private populateForm() {
    if (this.group) {
      this.groupForm.patchValue({
        name: this.group.name,
        description: '',
        externalId: this.group.externalId || '',
      })
    }
  }

  private loadGroupRolesAndUsers() {
    // Load actual group roles and users from the passed group data
    if (this.group) {
      // Select roles based on group's roles array
      this.selectedRoles = this.availableRoles.filter((role) =>
        this.group!.roles.some((groupRole) => groupRole.name === role.name)
      )

      // Mark roles as selected in availableRoles array
      this.availableRoles.forEach((role) => {
        role.selected = this.group!.roles.some((groupRole) => groupRole.name === role.name)
      })

      // Select users based on assignedUsers (which now contains UserInfo objects with accountSubjectId)
      this.selectedUsers = this.availableUsers.filter((user) =>
        this.group!.assignedUsers.some((u) => u.accountSubjectId === user.id)
      )

      // Mark users as selected in availableUsers array for UI checkbox state
      this.availableUsers.forEach((user) => {
        user.selected = this.group!.assignedUsers.some((u) => u.accountSubjectId === user.id)
      })
    }
  }

  private resetForm() {
    this.groupForm.reset()
    this.resetSelections()
    this.saving = false
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe()
      this.saveSubscription = null
    }
  }

  // Abstract method implementations
  getSaveButtonText(): string {
    return this.saving ? 'Saving...' : 'Save Changes'
  }

  performSave(groupData: any): Observable<any> {
    return this.apiService.updateAccountSubjectGroup(this.accountService.accountId, this.group!.id, groupData)
  }

  onSaveSuccess(result: any): void {
    this.groupUpdated.emit(this.group!)
    this.close()
  }

  deleteGroup() {
    if (!this.group) return

    this.groupToDelete = this.group
    this.deleteGroupDialog.open()
  }

  confirmDeleteGroup() {
    if (!this.groupToDelete) return

    this.apiService.deleteAccountSubjectGroup(this.accountService.accountId, this.groupToDelete.id).subscribe(
      () => {
        this.deleteGroupDialog.close()
        this.groupUpdated.emit(this.groupToDelete!) // Emit event to refresh parent component
        this.close()
      },
      handleArpioError(window.location.href, this.apiService, this.uiService)
    )
  }

  cancelDeleteGroup() {
    this.groupToDelete = null
    this.deleteGroupDialog.close()
  }

  customSort = this.accessLevelService.createAccessLevelSortFunction()

  filterRoles(event: any) {
    if (this.rolesTable) {
      this.rolesTable.filterGlobal(event.target.value, 'contains')
    }
  }

  filterUsers(event: any) {
    if (this.usersTable) {
      this.usersTable.filterGlobal(event.target.value, 'contains')
    }
  }
}
